import React, { useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { ArrowLeft, ArrowRight, RotateCcw, CalendarClock, CheckCircle2, AlertTriangle } from "lucide-react";
import { computeNewRegime, computeOldRegime, formatINR, advanceTaxSchedule } from "../../data/taxData";
import { ExpertHelpDialog } from "./ExpertHelpDialog";

export const AdvanceTaxCalculator = () => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ regime: "new", income: "", deductions: "", tds: "" });

  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const num = (v) => parseFloat(v) || 0;

  const result =
    form.regime === "new"
      ? computeNewRegime(num(form.income), false)
      : computeOldRegime(num(form.income), num(form.deductions), "<60", false);
  const netLiability = Math.max(0, result.total - num(form.tds));
  const applicable = netLiability >= 10000;
  const schedule = advanceTaxSchedule(netLiability);

  const reset = () => {
    setStep(0);
    setForm({ regime: "new", income: "", deductions: "", tds: "" });
  };

  return (
    <div data-testid="advance-tax-calculator">
      <div className="flex items-center gap-2 mb-6">
        {[0, 1].map((i) => (
          <div key={i} className={`h-2 rounded-full transition-all ${i <= step ? "bg-brand-orange w-8" : "bg-brand-border w-4"}`} />
        ))}
        <span className="text-sm text-brand-muted ml-2">Step {step + 1} of 2</span>
      </div>

      {step === 0 && (
        <Card className="bg-white border border-brand-border"><CardContent className="p-6 space-y-5">
          <h3 className="text-lg font-semibold text-brand-navy">Estimate Your Tax Liability — FY 2025-26</h3>
          <div>
            <Label>Tax Regime</Label>
            <div className="flex gap-2 mt-2">
              {[["new", "New Regime"], ["old", "Old Regime"]].map(([v, l]) => (
                <button
                  key={v}
                  data-testid={`atc-regime-${v}`}
                  onClick={() => set("regime")(v)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    form.regime === v
                      ? "bg-brand-orange text-white border-brand-orange"
                      : "bg-white text-brand-navy border-brand-border hover:border-brand-orange"
                  }`}
                >{l}</button>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="atc-income">Estimated Total Annual Income (₹)</Label>
            <Input id="atc-income" data-testid="atc-income" type="number" min="0" placeholder="0" className="mt-1 bg-white" value={form.income} onChange={(e) => set("income")(e.target.value)} />
            <p className="text-xs text-brand-muted mt-1">Business/professional income, capital gains, interest, rent, etc.</p>
          </div>
          {form.regime === "old" && (
            <div>
              <Label htmlFor="atc-deductions">Estimated Deductions — 80C, 80D etc. (₹)</Label>
              <Input id="atc-deductions" data-testid="atc-deductions" type="number" min="0" placeholder="0" className="mt-1 bg-white" value={form.deductions} onChange={(e) => set("deductions")(e.target.value)} />
            </div>
          )}
          <div>
            <Label htmlFor="atc-tds">TDS / TCS Already Deducted (₹)</Label>
            <Input id="atc-tds" data-testid="atc-tds" type="number" min="0" placeholder="0" className="mt-1 bg-white" value={form.tds} onChange={(e) => set("tds")(e.target.value)} />
          </div>
        </CardContent></Card>
      )}

      {step === 1 && (
        <div className="space-y-5">
          <Card className="bg-white border border-brand-border"><CardContent className="p-5">
            <div className="grid sm:grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-sm text-brand-muted">Estimated Total Tax</p>
                <p className="text-xl font-bold text-brand-navy" data-testid="atc-total-tax">{formatINR(result.total)}</p>
              </div>
              <div>
                <p className="text-sm text-brand-muted">Less: TDS/TCS</p>
                <p className="text-xl font-bold text-brand-navy">- {formatINR(num(form.tds))}</p>
              </div>
              <div>
                <p className="text-sm text-brand-muted">Net Advance Tax Payable</p>
                <p className="text-xl font-bold text-brand-orange" data-testid="atc-net-liability">{formatINR(netLiability)}</p>
              </div>
            </div>
          </CardContent></Card>

          {applicable ? (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3 items-start" data-testid="atc-applicable-msg">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-brand-navy">
                  Your net tax liability exceeds <strong>₹10,000</strong> — advance tax is applicable. Pay in installments below to avoid interest u/s 234B & 234C.
                </p>
              </div>
              <Card className="bg-white border border-brand-border"><CardContent className="p-5">
                <h4 className="font-semibold text-brand-navy mb-4 flex items-center gap-2">
                  <CalendarClock className="h-5 w-5 text-brand-orange" /> Installment Schedule
                </h4>
                <div className="space-y-3">
                  {schedule.map((s) => (
                    <div key={s.due} className="flex items-center justify-between bg-brand-grey rounded-lg p-4 border border-brand-border" data-testid={`atc-installment-${s.label.charAt(0)}`}>
                      <div>
                        <p className="font-medium text-brand-navy">{s.label} — {s.due}</p>
                        <p className="text-xs text-brand-muted">Cumulative: {s.cumulative * 100}% ({formatINR(netLiability * s.cumulative)})</p>
                      </div>
                      <span className="text-lg font-bold text-brand-orange">{formatINR(s.amount)}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-brand-muted mt-4">Taxpayers under presumptive taxation (44AD/44ADA) can pay 100% in one installment by 15th March.</p>
              </CardContent></Card>
            </>
          ) : (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex gap-3 items-start" data-testid="atc-not-applicable-msg">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
              <p className="text-sm text-brand-navy">
                Your net tax liability is below <strong>₹10,000</strong> — advance tax is <strong>not applicable</strong>. You can pay any balance as self-assessment tax while filing your return.
              </p>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-between mt-6">
        <Button variant="outline" data-testid="atc-back-btn" onClick={() => setStep(0)} disabled={step === 0} className="border-brand-border text-brand-navy hover:bg-brand-grey">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        {step === 0 ? (
          <Button data-testid="atc-next-btn" className="bg-brand-orange hover:bg-brand-orange-dark text-white" onClick={() => setStep(1)}>
            Calculate <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <div className="flex gap-3">
            <Button variant="outline" data-testid="atc-reset-btn" onClick={reset} className="border-brand-border text-brand-navy hover:bg-brand-grey">
              <RotateCcw className="h-4 w-4 mr-2" /> Start Over
            </Button>
            <ExpertHelpDialog
              service="Tax Planning"
              prefillDetails={`[Advance Tax Calculator - FY 2025-26]\nRegime: ${form.regime === "new" ? "New" : "Old"}\nEstimated Income: ${formatINR(num(form.income))}\nEstimated Total Tax: ${formatINR(result.total)}\nTDS/TCS: ${formatINR(num(form.tds))}\nNet Advance Tax Payable: ${formatINR(netLiability)}\nAdvance tax applicable: ${applicable ? "Yes" : "No"}\n\nI would like expert help with my advance tax planning.`}
            />
          </div>
        )}
      </div>
    </div>
  );
};
